import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { pollsService } from '@/services/polls.service';

export interface GroupPollBadge {
  /** Active polls in the group. */
  open: number;
  /** Active polls the user hasn't voted in. */
  unvoted: number;
}

interface PollBadgesValue {
  byGroup: Record<string, GroupPollBadge>;
  totalOpen: number;
  totalUnvoted: number;
}

const PollBadgesContext = createContext<PollBadgesValue>({
  byGroup: {},
  totalOpen: 0,
  totalUnvoted: 0,
});

export function usePollBadgesContext() {
  return useContext(PollBadgesContext);
}

/**
 * Open-poll badge counts for every group the user belongs to, across all
 * orgs (each request pinned to its owning org, events-calendar pattern).
 * Red = has an active poll the user hasn't voted in; gray = participated
 * in all open polls. Poll mutations invalidate ['poll-badges'].
 */
export function PollBadgesProvider({ children }: { children: ReactNode }) {
  const { organizations, isLoading, isSwitchingOrg } = useAuth();

  const groupsKey = useMemo(
    () =>
      organizations
        .flatMap((o) => (o.groups ?? []).map((g) => g.id))
        .sort()
        .join(','),
    [organizations]
  );

  const query = useQuery({
    queryKey: ['poll-badges', groupsKey],
    enabled: !isLoading && organizations.length > 0 && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<Record<string, GroupPollBadge>> => {
      const targets = organizations.flatMap((membership) =>
        (membership.groups ?? []).map((g) => ({
          groupId: g.id,
          orgId: membership.organization_id,
        }))
      );
      const results = await Promise.allSettled(
        targets.map(async ({ groupId, orgId }) => {
          const polls = await pollsService.getPolls(groupId, 'active', orgId);
          return {
            groupId,
            open: polls.length,
            unvoted: polls.filter((p) => !p.has_voted).length,
          };
        })
      );
      const byGroup: Record<string, GroupPollBadge> = {};
      for (const r of results) {
        // A failing group (e.g. revoked membership) just shows no badge
        if (r.status === 'fulfilled') {
          byGroup[r.value.groupId] = { open: r.value.open, unvoted: r.value.unvoted };
        }
      }
      return byGroup;
    },
  });

  const value = useMemo((): PollBadgesValue => {
    const byGroup = query.data ?? {};
    let totalOpen = 0;
    let totalUnvoted = 0;
    for (const badge of Object.values(byGroup)) {
      totalOpen += badge.open;
      totalUnvoted += badge.unvoted;
    }
    return { byGroup, totalOpen, totalUnvoted };
  }, [query.data]);

  return <PollBadgesContext.Provider value={value}>{children}</PollBadgesContext.Provider>;
}
